/**
 * unitCodeParser — pure text parsing for the paste-to-assign flow.
 *
 * Ops paste a call sheet or an email body; we recover which units are named,
 * what date range applies, and which site each unit sits under. Everything is
 * pure and deterministic — anything that depends on "now" takes it as a
 * parameter so tests never race the clock.
 */

import {
  buildUnitCodeRegex,
  classifyCodeMatch,
  knownCodeSet,
  stripUnitCodes,
  type ParseUnitCodesOptions,
  type UnitCodePartial,
} from './unitCodeMatch';

// One public API: the match rules and shorthand helpers live in their own modules.
export type { ParseUnitCodesOptions, UnitCodePartial } from './unitCodeMatch';
export {
  normalizeUnitCode,
  resolveUnitCodePartial,
  expandUnitCodeShorthand,
  type UnitCodePartialResolution,
  type ExpandedUnitCodeText,
} from './unitCodeShorthand';

/** Month abbreviations, index 0 = January. */
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** Display labels for the same months. */
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Whatever ops types between two dates. En dash, em dash, hyphen, or a word. */
const RANGE_SEPARATOR = '(?:–|—|-|to|thru|through|until|til)';

/** `Aug`, `August`, `Sept.` — captures the leading three letters. */
const MONTH_WORD = '(' + MONTH_KEYS.join('|') + ')[a-z]*\\.?';

/**
 * The day in a month-name date. The lookahead stops a longer number from being
 * read as a day and a fragment: `Aug 2027` must not parse as August 20th.
 */
const MONTH_DAY = '(\\d{1,2})(?!\\d)';

/**
 * The year written after a month-name date — `Aug 17 2027` or `Aug 17, 2027`.
 *
 * FOUR digits only. A two-digit trailing number in this form is far more often a
 * second day than a year (`Aug 17 26`), so it is left unread rather than guessed
 * at; the numeric `M/D/YY` form keeps its own two-digit handling.
 */
const WRITTEN_YEAR = '(?:,?\\s*(\\d{4})(?!\\d))?';

/** `M/D` with an optional year. Groups: month, day, year. */
// Anchored so a slashed number embedded in something longer is not read as a
// date: an aspect ratio in prose ("shot 2/1"), a fraction ("1/2 of the fleet")
// and a serial ("1234/5678") all reach this regex, and a false positive here
// silently rewrites the whole batch's date window.
const NUMERIC_DAY = '(?<![\\d/])(\\d{1,2})/(\\d{1,2})(?:/(\\d{2,4}))?(?![\\d/])';

/**
 * `Aug 17`, `Aug 17, 2027`, `Aug 17 – 24`, `Dec 28 2027 - Jan 3 2028`.
 * Groups: 1 month, 2 day, 3 year, 4 end month, 5 end day, 6 end year.
 */
const MONTH_DATE_RE = new RegExp(
  MONTH_WORD +
    '\\s+' +
    MONTH_DAY +
    WRITTEN_YEAR +
    '(?:\\s*' +
    RANGE_SEPARATOR +
    '\\s*(?:' +
    MONTH_WORD +
    '\\s+)?' +
    MONTH_DAY +
    WRITTEN_YEAR +
    ')?',
  'i',
);

/** `8/17`, `8/17/26`, `8/17-8/24`. Groups 1-3 are the start, 4-6 the end. */
const NUMERIC_DATE_RE = new RegExp(
  NUMERIC_DAY + '(?:\\s*' + RANGE_SEPARATOR + '\\s*' + NUMERIC_DAY + ')?',
  'i',
);

export interface ParsedUnitCodes {
  /** Unique full codes, upper-cased, number padded to 3 digits, in first-seen order. */
  codes: string[];
  /** Unique suffix-less shorthand (`1re1`), in first-seen order. */
  partials: UnitCodePartial[];
  /** How many times a code or shorthand repeated after its first occurrence. */
  duplicates: number;
}

/** Both fields are `YYYY-MM-DD`; `end` is `''` when only one date was written. */
export interface ParsedDateRange {
  start: string;
  end: string;
}

/**
 * Pull every unit code out of free text. Only codes built from a KNOWN model
 * code are returned — model codes are curated in the database. Suffix-less
 * shorthand comes back in `partials` for the caller to resolve.
 */
export function parseUnitCodes(text: string, modelCodes: string[], options?: ParseUnitCodesOptions): ParsedUnitCodes {
  const codes: string[] = [];
  const partials: UnitCodePartial[] = [];
  const seen = new Set<string>();
  let duplicates = 0;

  if (modelCodes.length === 0) return { codes, partials, duplicates };

  const known = knownCodeSet(options?.knownCodes);
  const regex = buildUnitCodeRegex(modelCodes);
  let match = regex.exec(text);
  while (match !== null) {
    const found = classifyCodeMatch(
      [match[1], match[2], match[3], match[4], match[5]],
      known,
      text.slice(match.index + match[0].length),
    );
    if (found.kind !== 'none') {
      const key = found.kind === 'full' ? found.code : found.partial.token;
      if (seen.has(key)) {
        duplicates += 1;
      } else {
        seen.add(key);
        if (found.kind === 'full') codes.push(found.code);
        else partials.push(found.partial);
      }
    }
    match = regex.exec(text);
  }

  return { codes, partials, duplicates };
}

/**
 * Optimal string alignment distance (Damerau-Levenshtein with adjacent
 * transposition). Callers only care whether two codes are within one edit, so a
 * length gap wider than one short-circuits to 3 rather than filling the matrix.
 */
export function damerauLevenshtein(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 1) return 3;

  const rows = a.length + 1;
  const cols = b.length + 1;
  const grid: number[][] = [];
  for (let i = 0; i < rows; i += 1) grid.push(new Array<number>(cols).fill(0));
  for (let i = 0; i < rows; i += 1) grid[i][0] = i;
  for (let j = 0; j < cols; j += 1) grid[0][j] = j;

  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(grid[i - 1][j] + 1, grid[i][j - 1] + 1, grid[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, grid[i - 2][j - 2] + 1);
      }
      grid[i][j] = best;
    }
  }

  return grid[rows - 1][cols - 1];
}

/** Nearest known code within a single edit, or null — a wrong guess is worse than none. */
export function suggestUnitCode(code: string, knownCodes: string[]): string | null {
  let best: string | null = null;
  let bestDistance = 2;
  for (const known of knownCodes) {
    const distance = damerauLevenshtein(code, known);
    if (distance <= 1 && distance < bestDistance) {
      bestDistance = distance;
      best = known;
      if (distance === 0) break;
    }
  }

  return best;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** Month lengths in a common year, index 0 = January. */
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Proleptic Gregorian rule — the same one Postgres applies to a `date`. */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  return month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];
}

/**
 * `''` for anything that is not a real calendar date.
 *
 * The day is bounded by the ACTUAL length of that month, leap years included:
 * `2/30`, `4/31` and `Feb 31` used to survive as far as the assignment payload,
 * where the shape-only checks upstream pass them and only Postgres rejects the
 * write — after the placement has already committed.
 */
function toIsoDate(year: number, month: number, day: number): string {
  if (!Number.isInteger(year) || year < 1) return '';
  if (!Number.isInteger(month) || month < 1 || month > 12) return '';
  if (!Number.isInteger(day) || day < 1 || day > daysInMonth(year, month)) return '';
  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
}

function parseIsoParts(iso: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** `aug`, `August`, `Sept.` all resolve to 8. Returns 0 when unrecognised. */
function monthNumber(word: string): number {
  return MONTH_KEYS.indexOf(word.slice(0, 3).toLowerCase()) + 1;
}

/**
 * Two-digit years are this century — nobody is pasting 1926 call sheets.
 * Four-digit years are taken verbatim; the month-name form only ever passes four.
 */
function resolveYear(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const value = Number(raw);
  if (!Number.isFinite(value)) return fallback;
  return raw.length === 2 ? 2000 + value : value;
}

/**
 * A range whose end reads earlier than its start crossed New Year: `Dec 28 – Jan 3`
 * is next January, not ten months backwards. Roll the end forward a year.
 *
 * Only ever called when the end's year was INFERRED. A year the operator actually
 * typed is theirs, typo and all — silently rewriting it would hide their mistake.
 */
function rollEndPastNewYear(endIso: string, startIso: string): string {
  const end = parseIsoParts(endIso);
  const start = parseIsoParts(startIso);
  // Both sides are zero-padded `YYYY-MM-DD`, so a plain string compare is a date compare.
  if (!end || !start || endIso >= startIso) return endIso;
  return toIsoDate(end.year + 1, end.month, end.day);
}

/**
 * Find the date range in a paste.
 *
 * Unit codes are blanked out first: `Aug 17 – 1RE191TCA` would otherwise read
 * the `1` out of the code and report an end date of Aug 1.
 */
export function parseDateRange(
  text: string,
  modelCodes: string[],
  now?: Date,
  options?: ParseUnitCodesOptions,
): ParsedDateRange | null {
  const cleaned = stripUnitCodes(text, modelCodes, knownCodeSet(options?.knownCodes));
  const defaultYear = (now ?? new Date()).getFullYear();

  const named = MONTH_DATE_RE.exec(cleaned);
  if (named) {
    const startMonth = monthNumber(named[1]);
    // A year the operator wrote out wins; otherwise the paste is read as this year.
    const startYear = resolveYear(named[3], defaultYear);
    const start = toIsoDate(startYear, startMonth, Number(named[2]));
    if (start) {
      const endMonth = named[4] ? monthNumber(named[4]) : startMonth;
      let end = '';
      if (named[5]) {
        // An undated end sits in the start's year — `Aug 17 2027 – 24` is all 2027.
        end = toIsoDate(resolveYear(named[6], startYear), endMonth, Number(named[5]));
        // named[6] is a year the operator typed — leave it exactly as written.
        if (!named[6]) end = rollEndPastNewYear(end, start);
      }
      return { start, end };
    }
  }

  const numeric = NUMERIC_DATE_RE.exec(cleaned);
  if (numeric) {
    const startYear = resolveYear(numeric[3], defaultYear);
    const start = toIsoDate(startYear, Number(numeric[1]), Number(numeric[2]));
    if (start) {
      let end = '';
      if (numeric[4] && numeric[5]) {
        end = toIsoDate(resolveYear(numeric[6], startYear), Number(numeric[4]), Number(numeric[5]));
        // numeric[6] is a year the operator typed — leave it exactly as written.
        if (!numeric[6]) end = rollEndPastNewYear(end, start);
      }
      return { start, end };
    }
  }

  return null;
}

/** True when the range has already begun — compared at local midnight. */
export function isDateLive(startIso: string, now?: Date): boolean {
  const parts = parseIsoParts(startIso);
  if (!parts) return false;

  const today = now ?? new Date();
  const startAt = new Date(parts.year, parts.month - 1, parts.day).getTime();
  const todayAt = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return startAt <= todayAt;
}

function ordinalSuffix(day: number): string {
  const teens = day % 100;
  if (teens >= 11 && teens <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** `2026-08-16` → `Aug 16th, 2026`. Empty string for anything unparseable. */
export function formatLongDate(iso: string): string {
  const parts = parseIsoParts(iso);
  if (!parts) return '';
  return `${MONTH_LABELS[parts.month - 1]} ${parts.day}${ordinalSuffix(parts.day)}, ${parts.year}`;
}

function shortDate(parts: { month: number; day: number }): string {
  return `${MONTH_LABELS[parts.month - 1]} ${parts.day}`;
}

/** Compact range label: `Aug 17 – 24`, `Aug 17 – Sep 3`, `From Aug 17`, `Until Aug 24`. */
export function formatDayRange(start: string, end: string): string {
  const from = parseIsoParts(start);
  const to = parseIsoParts(end);

  if (!from && !to) return '';
  if (from && !to) return `From ${shortDate(from)}`;
  if (!from && to) return `Until ${shortDate(to)}`;
  if (!from || !to) return '';

  return from.month === to.month ? `${shortDate(from)} – ${to.day}` : `${shortDate(from)} – ${shortDate(to)}`;
}

/** Comparison form for names: `Basecamp — Alpha!` → `BASECAMP ALPHA`. */
export function normalizeName(s: string): string {
  return s
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Map each unit code to the site named on the same line. A paste is usually
 * grouped — `Basecamp Alpha: 1RE191TCA, 2RE066TCA` — so a line naming a site
 * scopes the codes on THAT line to it. Codes on lines with no site name are
 * left out for the caller to default. The first mention of a code wins, so a
 * later re-list cannot silently move a unit.
 */
export function scopeLinesToSites<T extends { id: string; name: string }>(
  text: string,
  modelCodes: string[],
  sites: T[],
): Map<string, string> {
  const scoped = new Map<string, string>();

  // Longest name first, so "Basecamp Alpha" beats a site merely called "Basecamp".
  const needles = sites
    .map((site) => ({ id: site.id, needle: normalizeName(site.name) }))
    .filter((site) => site.needle.length > 0)
    .sort((a, b) => b.needle.length - a.needle.length);

  if (needles.length === 0) return scoped;

  for (const line of text.split(/\r?\n/)) {
    const haystack = ` ${normalizeName(line)} `;
    const site = needles.find((candidate) => haystack.includes(` ${candidate.needle} `));
    if (!site) continue;

    for (const code of parseUnitCodes(line, modelCodes).codes) {
      if (!scoped.has(code)) scoped.set(code, site.id);
    }
  }

  return scoped;
}
